(() => {
  'use strict';

  /*
   * ZYVIRA CART REWARDS
   * ------------------------------------------------------------
   * Safe, isolated enhancement for the existing cart page/drawer.
   * It does NOT change line items, quantities, prices, discounts,
   * checkout buttons, ZYVIRA10, free-shipping logic or inventory logic.
   */

  const ROOT = window.Shopify?.routes?.root || '/';

  const CONFIG = Object.freeze({
    thresholds: Object.freeze({
      shipping: 79900,
      organizer: 149900,
      surprise: 349900
    }),

    /*
     * Eligibility uses Shopify's cart.total_price in paise.
     * This matches the subtotal currently displayed by Zyvira's cart.
     */
    totalField: 'total_price',

    /*
     * This public cart attribute carries into the order so the packing
     * team can see exactly which gift must be included.
     */
    attributeName: 'Zyvira Gift Eligibility',

    cartUrl: `${ROOT}cart.js`,
    updateUrl: `${ROOT}cart/update.js`
  });

  const SELECTORS = Object.freeze({
    scopes: 'ajax-cart.mini-cart, .cart-page',
    mutationButtons: [
      '.qty-bt',
      'delpro-btn',
      '.remove-product',
      '[name="add"]',
      '.quick-add-btn',
      'cart-remove-button'
    ].join(','),
    quantityInputs: '.qty-box-input, [name="updates[]"]'
  });

  const ATTRIBUTE_VALUES = Object.freeze({
    organizer: 'FREE Mini Jewellery Travel Organizer worth ₹349',
    both: 'FREE Mini Jewellery Travel Organizer + Premium Surprise Gift worth ₹649'
  });

  let fetchSequence = 0;
  let refreshTimer = 0;
  let attributeSyncPromise = null;
  let queuedAttributeValue = null;
  let observer = null;

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const setText = (element, value) => {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  };

  const formatMoney = (cents, currency = 'INR') => {
    const amount = Math.max(0, toNumber(cents)) / 100;
    const hasDecimals = Math.round(amount) !== amount;

    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return `₹${amount.toFixed(hasDecimals ? 2 : 0)}`;
    }
  };

  const getEligibilityTotal = (cart) => {
    return Math.max(0, toNumber(cart?.[CONFIG.totalField]));
  };

  const getTier = (total) => {
    if (total >= CONFIG.thresholds.surprise) {
      return {
        key: 'both',
        attributeValue: ATTRIBUTE_VALUES.both
      };
    }

    if (total >= CONFIG.thresholds.organizer) {
      return {
        key: 'organizer',
        attributeValue: ATTRIBUTE_VALUES.organizer
      };
    }

    return {
      key: 'none',
      attributeValue: ''
    };
  };

  const cardMarkup = () => `
    <div class="zyvira-rewards-card__header">
      <span class="zyvira-rewards-card__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 10h16v10H4zM3 7h18v3H3zM12 7v13M7.5 7C5.2 7 4 5.9 4 4.7 4 3.6 4.9 3 5.9 3 8 3 10 5.2 12 7M16.5 7C18.8 7 20 5.9 20 4.7 20 3.6 19.1 3 18.1 3 16 3 14 5.2 12 7"
            stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>

      <div class="zyvira-rewards-card__content">
        <span class="zyvira-rewards-card__eyebrow">Zyvira rewards</span>
        <p class="zyvira-rewards-card__title" data-zyvira-reward-title></p>
        <p class="zyvira-rewards-card__copy" data-zyvira-reward-copy></p>
      </div>
    </div>

    <div
      class="zyvira-rewards-card__progress"
      data-zyvira-reward-progress
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow="0"
      aria-label="Progress toward Zyvira cart rewards"
    >
      <span class="zyvira-rewards-card__progress-fill"></span>
    </div>

    <div class="zyvira-rewards-card__milestones" aria-label="Zyvira reward milestones">
       <div class="zyvira-reward-milestone" data-reward-milestone="organizer">
        <span class="zyvira-reward-milestone__icon" aria-hidden="true">1</span>
        <span class="zyvira-reward-milestone__label">Travel Mini Organizer - FREE</span>
        <span class="zyvira-reward-milestone__amount">Worth ₹349</span>
      </div>

      <div class="zyvira-reward-milestone" data-reward-milestone="surprise">
        <span class="zyvira-reward-milestone__icon" aria-hidden="true">2</span>
        <span class="zyvira-reward-milestone__label">Zyvira Surprise Gift</span>
        <span class="zyvira-reward-milestone__amount">Worth ₹649</span>
      </div>
    </div>

    <p class="zyvira-rewards-card__note">
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 12.5l4 4L19 6.5" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Unlocked gifts are saved with your cart and included by Zyvira during packing.</span>
    </p>
  `;

  const createCard = () => {
    const card = document.createElement('section');
    card.className = 'zyvira-rewards-card';
    card.dataset.zyviraRewardsCard = '';
    card.dataset.rewardTier = 'none';
    card.setAttribute('aria-label', 'Zyvira cart rewards');
    card.setAttribute('aria-live', 'polite');
    card.innerHTML = cardMarkup();
    return card;
  };

  const placeCard = (scope, card) => {
    if (!scope || !card) return false;

    const shippingBar = scope.querySelector('.shipping-bar');
    if (shippingBar?.parentElement) {
      if (shippingBar.nextElementSibling !== card) {
        shippingBar.insertAdjacentElement('afterend', card);
      }
      return true;
    }

    if (scope.matches('ajax-cart.mini-cart')) {
      const cartItems = scope.querySelector('cart-pro');
      if (cartItems?.parentElement) {
        cartItems.insertAdjacentElement('beforebegin', card);
        return true;
      }
    }

    const pageCartBody = scope.querySelector('ajax-items.cart-body');
    const pageAnchor = pageCartBody?.querySelector('.cart-empty-title, .cart-body-item');
    if (pageAnchor?.parentElement) {
      pageAnchor.insertAdjacentElement('beforebegin', card);
      return true;
    }

    return false;
  };

  const ensureCard = (scope) => {
    let card = scope.querySelector('[data-zyvira-rewards-card]');

    if (!card) {
      card = createCard();
      if (!placeCard(scope, card)) {
        card.remove();
        return null;
      }
    } else {
      placeCard(scope, card);
    }

    return card;
  };

  const setMilestoneState = (card, milestoneName, state) => {
    const milestone = card.querySelector(
      `[data-reward-milestone="${milestoneName}"]`
    );

    if (!milestone) return;

    milestone.classList.remove('is-done', 'is-active', 'is-locked');
    milestone.classList.add(`is-${state}`);
  };

  const updateCard = (card, cart) => {
    const total = getEligibilityTotal(cart);
    const currency = cart?.currency || 'INR';
    const tier = getTier(total);

    const title = card.querySelector('[data-zyvira-reward-title]');
    const copy = card.querySelector('[data-zyvira-reward-copy]');
    const progress = card.querySelector('[data-zyvira-reward-progress]');

    let titleText = '';
    let copyText = '';
    let ariaText = '';

    if (tier.key === 'both') {
      titleText = 'You’ve unlocked both Zyvira gifts!';
      copyText =
        'Your FREE Mini Jewellery Travel Organizer and Premium Surprise Gift worth ₹649 will be included with your order.';
      ariaText = 'All Zyvira cart rewards unlocked.';
    } else if (tier.key === 'organizer') {
      const remaining = CONFIG.thresholds.surprise - total;
      titleText = 'Your travel treat is unlocked! ✨';
      copyText =
        `Add ${formatMoney(remaining, currency)} more to unlock your Premium Surprise Gift worth ₹649.`;
      ariaText =
        `${formatMoney(remaining, currency)} remaining to unlock the premium surprise gift.`;
    } else {
      const remaining = Math.max(0, CONFIG.thresholds.organizer - total);
      titleText = 'Your first Zyvira gift is almost yours';
      copyText =
        `Add ${formatMoney(remaining, currency)} more to receive a FREE Mini Jewellery Travel Organizer worth ₹349.`;
      ariaText =
        `${formatMoney(remaining, currency)} remaining to unlock the free travel organizer.`;
    }

    setText(title, titleText);
    setText(copy, copyText);

    const progressPercent = CONFIG.thresholds.surprise > 0
      ? Math.min(100, Math.max(0, (total / CONFIG.thresholds.surprise) * 100))
      : 100;

    card.style.setProperty('--zyv-reward-progress', `${progressPercent.toFixed(2)}%`);
    card.dataset.rewardTier = tier.key;

    if (progress) {
      progress.setAttribute('aria-valuenow', String(Math.round(progressPercent)));
      progress.setAttribute('aria-valuetext', ariaText);
    }

    if (total < CONFIG.thresholds.shipping) {
      setMilestoneState(card, 'shipping', 'active');
      setMilestoneState(card, 'organizer', 'locked');
      setMilestoneState(card, 'surprise', 'locked');
    } else if (total < CONFIG.thresholds.organizer) {
      setMilestoneState(card, 'shipping', 'done');
      setMilestoneState(card, 'organizer', 'active');
      setMilestoneState(card, 'surprise', 'locked');
    } else if (total < CONFIG.thresholds.surprise) {
      setMilestoneState(card, 'shipping', 'done');
      setMilestoneState(card, 'organizer', 'done');
      setMilestoneState(card, 'surprise', 'active');
    } else {
      setMilestoneState(card, 'shipping', 'done');
      setMilestoneState(card, 'organizer', 'done');
      setMilestoneState(card, 'surprise', 'done');
    }

    return tier;
  };

  const ensureCheckoutAttributeInput = (scope, value) => {
    const forms = scope.querySelectorAll('form#cart, form#ajaxcart-form');

    forms.forEach((form) => {
      let input = form.querySelector('[data-zyvira-reward-attribute]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `attributes[${CONFIG.attributeName}]`;
        input.dataset.zyviraRewardAttribute = '';
        form.appendChild(input);
      }

      input.value = value;
    });
  };

  const syncAttribute = async (cart, desiredValue) => {
    const currentValue = String(
      cart?.attributes?.[CONFIG.attributeName] || ''
    );

    if (currentValue === desiredValue) return;

    if (attributeSyncPromise) {
      queuedAttributeValue = desiredValue;
      return attributeSyncPromise;
    }

    attributeSyncPromise = fetch(CONFIG.updateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          [CONFIG.attributeName]: desiredValue
        }
      })
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart attribute update failed: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.warn('[Zyvira rewards] Unable to save gift eligibility.', error);
      })
      .finally(() => {
        attributeSyncPromise = null;

        if (queuedAttributeValue !== null) {
          const nextValue = queuedAttributeValue;
          queuedAttributeValue = null;
          scheduleRefresh(40);

          /*
           * A fresh cart fetch will compare and save the queued value.
           * This avoids overlapping cart/update.js requests.
           */
          if (nextValue !== desiredValue) {
            scheduleRefresh(80);
          }
        }
      });

    return attributeSyncPromise;
  };

  const render = (cart) => {
    const itemCount = Math.max(0, toNumber(cart?.item_count));
    const total = getEligibilityTotal(cart);
    const tier = getTier(total);
    const scopes = document.querySelectorAll(SELECTORS.scopes);

    scopes.forEach((scope) => {
      const card = scope.querySelector('[data-zyvira-rewards-card]');

      if (itemCount <= 0) {
        card?.remove();
        ensureCheckoutAttributeInput(scope, '');
        return;
      }

      const ensuredCard = ensureCard(scope);
      if (ensuredCard) updateCard(ensuredCard, cart);
      ensureCheckoutAttributeInput(scope, tier.attributeValue);
    });

    /*
     * Background synchronization protects accelerated-checkout paths.
     * The hidden form input is a second safety layer for normal checkout.
     */
    void syncAttribute(cart, itemCount > 0 ? tier.attributeValue : '');
  };

  const fetchCart = async () => {
    const sequence = ++fetchSequence;

    try {
      const response = await fetch(CONFIG.cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();
      if (sequence !== fetchSequence) return;

      render(cart);
    } catch (error) {
      console.warn('[Zyvira rewards] Unable to refresh cart rewards.', error);
    }
  };

  const scheduleRefresh = (delay = 120) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(fetchCart, delay);
  };

  const scheduleCartMutationRefreshes = () => {
    /*
     * Zyvira's current cart rerenders asynchronously.
     * The staggered checks catch both fast and delayed drawer/page updates.
     */
    scheduleRefresh(120);
    window.setTimeout(fetchCart, 620);
    window.setTimeout(fetchCart, 1250);
  };

  const handleClick = (event) => {
    if (event.target.closest(SELECTORS.mutationButtons)) {
      scheduleCartMutationRefreshes();
    }
  };

  const handleChange = (event) => {
    if (event.target.matches?.(SELECTORS.quantityInputs)) {
      scheduleCartMutationRefreshes();
    }
  };

  const mutationIsRelevant = (mutation) => {
    const target = mutation.target instanceof Element
      ? mutation.target
      : mutation.target?.parentElement;

    if (target?.closest?.('ajax-cart.mini-cart, .cart-page')) return true;

    return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
      if (!(node instanceof Element)) return false;

      return node.matches?.(
        'ajax-cart.mini-cart, .cart-page, .js-contents, cart-pro, ajax-items, .shipping-bar'
      ) || Boolean(
        node.querySelector?.(
          'ajax-cart.mini-cart, .cart-page, .js-contents, cart-pro, ajax-items, .shipping-bar'
        )
      );
    });
  };

  const initialize = () => {
    document.addEventListener('click', handleClick, true);
    document.addEventListener('change', handleChange, true);

    [
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'ajaxProduct:added',
      'product:added'
    ].forEach((eventName) => {
      document.addEventListener(eventName, scheduleCartMutationRefreshes);
    });

    observer = new MutationObserver((mutations) => {
      if (mutations.some(mutationIsRelevant)) {
        scheduleRefresh(80);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    void fetchCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
